import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Box, Zap, Play, RotateCcw } from "lucide-react";
import * as THREE from "three";
import AdReward from "./_adReward";

/**
 * Jixxeo Cube — a real 3D endless runner. Steer a neon cube left/right
 * through spawning obstacles. WebGL via three.js + @react-three/fiber.
 * Arrow keys or A/D to steer. Score increases with distance.
 */

const LANES = [-2.4, 0, 2.4];

function Player({ laneRef, alive }) {
  const ref = useRef();
  useFrame((_, dt) => {
    if (!ref.current) return;
    const target = LANES[laneRef.current];
    ref.current.position.x += (target - ref.current.position.x) * Math.min(1, dt * 12);
    ref.current.rotation.y += dt * 2;
    ref.current.rotation.x += dt * 1.4;
    if (alive) ref.current.position.y = 0.5 + Math.sin(performance.now() / 260) * 0.06;
  });
  return (
    <mesh ref={ref} position={[0, 0.5, 4]} castShadow>
      <boxGeometry args={[1, 1, 1]} />
      <meshStandardMaterial color="#00F0FF" emissive="#00F0FF" emissiveIntensity={0.9} metalness={0.4} roughness={0.2} />
    </mesh>
  );
}

function ObstacleGroup({ obsRef }) {
  const groupRef = useRef();
  useFrame((_, dt) => {
    if (!groupRef.current) return;
    // sync children with ref list
    const list = obsRef.current;
    // rotate each mesh
    groupRef.current.children.forEach((m, i) => {
      const data = list[i];
      if (!data) return;
      m.visible = true;
      m.position.set(LANES[data.lane], 0.8, data.z);
      m.rotation.y += dt * 1.2;
    });
    for (let i = list.length; i < groupRef.current.children.length; i++) {
      groupRef.current.children[i].visible = false;
    }
  });
  // pre-instantiate 32 slots
  const slots = useMemo(() => Array.from({ length: 32 }), []);
  const COLORS = ["#39FF14", "#FFE300", "#FF003C", "#8A2BE2"];
  return (
    <group ref={groupRef}>
      {slots.map((_, i) => (
        <mesh key={i} visible={false} castShadow>
          <boxGeometry args={[1.4, 1.4, 1.4]} />
          <meshStandardMaterial color={COLORS[i % 4]} emissive={COLORS[i % 4]} emissiveIntensity={0.7} metalness={0.3} roughness={0.4} />
        </mesh>
      ))}
    </group>
  );
}

function Corridor({ obsRef, floorRef }) {
  const gridRef = useRef();
  useFrame((_, dt) => {
    if (gridRef.current) {
      gridRef.current.position.z = floorRef.current;
    }
  });
  return (
    <group>
      {/* floor */}
      <mesh position={[0, 0, -30]} rotation-x={-Math.PI / 2} receiveShadow>
        <planeGeometry args={[9, 200]} />
        <meshStandardMaterial color="#0a0a12" />
      </mesh>
      <group ref={gridRef}>
        {Array.from({ length: 24 }).map((_, i) => (
          <mesh key={i} position={[0, 0.01, -i * 4]} rotation-x={-Math.PI / 2}>
            <planeGeometry args={[8, 0.06]} />
            <meshBasicMaterial color="#00F0FF" transparent opacity={0.55} />
          </mesh>
        ))}
      </group>
      {/* walls */}
      {[-4.5, 4.5].map((x) => (
        <mesh key={x} position={[x, 1, -30]}>
          <planeGeometry args={[200, 3]} />
          <meshBasicMaterial color="#FF003C" transparent opacity={0.08} side={THREE.DoubleSide} />
        </mesh>
      ))}
      <ObstacleGroup obsRef={obsRef} />
    </group>
  );
}

function GameScene({ laneRef, aliveRef, onCrash, onScore, speedRef, obsRef, floorRef }) {
  const spawnTimerRef = useRef(0);
  const seedRef = useRef(1);

  useFrame((_, dt) => {
    const speed = speedRef.current;
    const list = obsRef.current;
    // scroll obstacles
    for (const o of list) o.z += dt * speed;
    // remove passed ones
    obsRef.current = list.filter((o) => o.z <= 8);
    // ground scroll
    floorRef.current = (floorRef.current + dt * speed) % 4;
    // spawn
    spawnTimerRef.current -= dt;
    if (aliveRef.current && spawnTimerRef.current <= 0) {
      spawnTimerRef.current = 0.45 + Math.random() * 0.25;
      // pick 1-2 obstacle lanes, always leave at least one clear
      const shuffled = [0, 1, 2].sort(() => Math.random() - 0.5);
      const count = Math.random() < 0.55 ? 1 : 2;
      const lanes = shuffled.slice(0, count);
      lanes.forEach((lane) => {
        seedRef.current += 1;
        obsRef.current.push({ id: seedRef.current, lane, z: -60 });
      });
    }
    if (aliveRef.current) onScore(dt * speed);
    // collision
    if (aliveRef.current) {
      for (const o of obsRef.current) {
        if (Math.abs(o.z - 4) < 0.9 && o.lane === laneRef.current) {
          onCrash();
          break;
        }
      }
    }
  });

  return (
    <>
      <color attach="background" args={["#050510"]} />
      <fog attach="fog" args={["#050510", 5, 60]} />
      <ambientLight intensity={0.35} />
      <pointLight position={[0, 6, 4]} intensity={1.4} color="#00F0FF" distance={20} />
      <pointLight position={[0, 4, -12]} intensity={2} color="#FF003C" distance={30} />
      <Player laneRef={laneRef} alive={aliveRef.current} />
      <Corridor obsRef={obsRef} floorRef={floorRef} />
    </>
  );
}

export default function JixxeoCube() {
  const [running, setRunning] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [best, setBest] = useState(() => Number(localStorage.getItem("jixxeo_cube_best") || 0));
  const laneRef = useRef(1);
  const aliveRef = useRef(false);
  const speedRef = useRef(14);
  const scoreAcc = useRef(0);
  const obsRef = useRef([]);
  const floorRef = useRef(0);

  useEffect(() => {
    if (score > best) {
      setBest(score);
      localStorage.setItem("jixxeo_cube_best", String(score));
    }
  }, [score, best]);

  useEffect(() => {
    const onKey = (e) => {
      if (!running) return;
      if (e.key === "ArrowLeft" || e.key.toLowerCase() === "a") laneRef.current = Math.max(0, laneRef.current - 1);
      if (e.key === "ArrowRight" || e.key.toLowerCase() === "d") laneRef.current = Math.min(2, laneRef.current + 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [running]);

  // gradually accelerate
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => { speedRef.current = Math.min(28, speedRef.current + 0.35); }, 1500);
    return () => clearInterval(id);
  }, [running]);

  const start = () => {
    laneRef.current = 1; aliveRef.current = true;
    speedRef.current = 14; scoreAcc.current = 0;
    obsRef.current = [];
    setScore(0); setGameOver(false); setRunning(true);
  };

  const crash = () => {
    if (!aliveRef.current) return;
    aliveRef.current = false;
    setRunning(false); setGameOver(true);
  };

  const addScore = (n) => {
    scoreAcc.current += n;
    if (scoreAcc.current > 1) {
      const inc = Math.floor(scoreAcc.current);
      scoreAcc.current -= inc;
      setScore((s) => s + inc);
    }
  };

  return (
    <div className="min-h-screen bg-[#050510] text-white p-4 md:p-8 relative overflow-hidden flex flex-col">
      <div className="relative max-w-5xl w-full mx-auto flex flex-col">
        <div className="flex items-center gap-3 mb-4">
          <span className="w-10 h-10 rounded-xl bg-[#39FF14] flex items-center justify-center shadow-[0_0_24px_rgba(57,255,20,0.55)]">
            <Box className="w-5 h-5 text-black" strokeWidth={3} />
          </span>
          <div>
            <div className="mono text-[9px] uppercase tracking-[0.25em] text-[#00F0FF]">// jixxeo apps · demo pwa · webgl</div>
            <h1 className="font-heading font-black text-2xl md:text-3xl uppercase tracking-tighter leading-none">Jixxeo Cube</h1>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Chip label="Distance" value={score} color="#00F0FF" />
            <Chip label="Best" value={best} color="#39FF14" />
          </div>
        </div>

        <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-black" style={{ aspectRatio: "16 / 9" }}>
          <Canvas
            camera={{ position: [0, 3.5, 8], fov: 65 }}
            gl={{ antialias: true, powerPreference: "high-performance" }}
            shadows
          >
            <Suspense fallback={null}>
              <GameScene
                laneRef={laneRef} aliveRef={aliveRef}
                onCrash={crash} onScore={addScore} speedRef={speedRef}
                obsRef={obsRef} floorRef={floorRef}
              />
            </Suspense>
          </Canvas>

          {/* Touch controls */}
          <div className="absolute inset-x-0 bottom-3 flex items-center justify-between px-4 md:hidden">
            <button onClick={() => (laneRef.current = Math.max(0, laneRef.current - 1))}
              className="w-14 h-14 rounded-2xl border border-[#00F0FF]/40 bg-black/60 text-[#00F0FF] font-heading font-black text-2xl">◀</button>
            <button onClick={() => (laneRef.current = Math.min(2, laneRef.current + 1))}
              className="w-14 h-14 rounded-2xl border border-[#00F0FF]/40 bg-black/60 text-[#00F0FF] font-heading font-black text-2xl">▶</button>
          </div>

          {(!running || gameOver) && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm">
              <div className="text-center max-w-md px-4">
                <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#39FF14] mb-2">// endless run</div>
                <div className="font-heading font-black text-4xl uppercase tracking-tighter mb-2">
                  {gameOver ? "Crashed" : "Steer the cube"}
                </div>
                {gameOver && (
                  <div className="mono text-sm text-white/70 mb-3">Distance <span className="text-[#00F0FF] font-bold">{score}</span> · Best <span className="text-[#39FF14] font-bold">{best}</span></div>
                )}
                {gameOver && (
                  <div className="mb-4 flex justify-center"><AdReward game="jixxeo-cube" compact /></div>
                )}
                <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-6">
                  A / D or arrows to switch lane · avoid the blocks
                </div>
                <button data-testid="embed-cube-start" onClick={start}
                  className="inline-flex items-center gap-2 bg-[#39FF14] hover:bg-white text-black font-black mono text-xs uppercase tracking-widest px-8 py-3 rounded-full pulse-neon">
                  {gameOver ? <RotateCcw className="w-4 h-4" /> : <Play className="w-4 h-4" fill="#000" />} {gameOver ? "Retry" : "Start"}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="mono text-[9px] uppercase tracking-widest text-white/40 mt-4 text-center flex items-center gap-2 justify-center">
          <Zap className="w-3 h-3" /> three.js webgl · real 3D scene · runs entirely in browser
        </div>
      </div>
    </div>
  );
}

function Chip({ label, value, color }) {
  return (
    <div className="hidden md:inline-flex items-center gap-2 mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border" style={{ borderColor: `${color}55`, background: `${color}10`, color }}>
      <span className="opacity-70">{label}</span>
      <span className="font-bold text-white">{value}</span>
    </div>
  );
}
